import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getUsers, deleteUser, toggleUserStatus } from '../../services/adminService';
import { User, UserType, UserFilters, PaginatedResponse } from '../../types/admin';
import { UserForm } from './UserForm';
import { UserDetailView } from './UserDetailView';
import { VerificationModal } from './VerificationModal';
import './UsersList.css';

export const UsersList: React.FC = () => {
	const { token } = useAuth();
	const [users, setUsers] = useState<PaginatedResponse<User> | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string>('');
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize] = useState(20);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showDetailModal, setShowDetailModal] = useState(false);
	const [showVerificationModal, setShowVerificationModal] = useState(false);
	const [selectedUser, setSelectedUser] = useState<User | null>(null);

	// Filters
	const [filters] = useState<UserFilters>({});
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedType, setSelectedType] = useState<UserType | ''>('');
	const [selectedStatus, setSelectedStatus] = useState<boolean | ''>('');

	const loadUsers = useCallback(
		async (page: number = currentPage) => {
			if (!token) return;

			try {
				setLoading(true);
				setError('');

				const filterParams: UserFilters = {
					...filters,
					...(searchTerm && { search: searchTerm }),
					...(selectedType && { type: selectedType }),
					...(selectedStatus !== '' && { isActive: selectedStatus }),
				};

				const data = await getUsers(token, page, pageSize, filterParams);
				setUsers(data);
			} catch (err) {
				setError('Greška pri učitavanju korisnika');
				console.error('Users loading error:', err);
			} finally {
				setLoading(false);
			}
		},
		[token, currentPage, pageSize, filters, searchTerm, selectedType, selectedStatus]
	);

	useEffect(() => {
		loadUsers();
	}, [loadUsers]);

	const handleSearch = () => {
		setCurrentPage(1);
		loadUsers(1);
	};

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		loadUsers(page);
	};

	const handleCreateUser = () => {
		setSelectedUser(null);
		setShowCreateModal(true);
	};

	const handleEditUser = (user: User) => {
		setSelectedUser(user);
		setShowEditModal(true);
	};

	const handleViewUser = (user: User) => {
		setSelectedUser(user);
		setShowDetailModal(true);
	};

	const handleViewVerification = (user: User) => {
		setSelectedUser(user);
		setShowVerificationModal(true);
	};

	const handleDeleteUser = async (userId: string) => {
		if (!token) return;

		if (window.confirm('Da li ste sigurni da želite da obrišete ovog korisnika?')) {
			try {
				await deleteUser(token, userId);
				loadUsers();
			} catch (err) {
				setError('Greška pri brisanju korisnika');
				console.error('Delete user error:', err);
			}
		}
	};

	const handleToggleStatus = async (userId: string) => {
		if (!token) return;

		try {
			await toggleUserStatus(token, userId);
			loadUsers();
		} catch (err) {
			setError('Greška pri promeni statusa korisnika');
			console.error('Toggle status error:', err);
		}
	};

	const getUserTypeLabel = (type: UserType) => {
		switch (type) {
			case UserType.ADMIN:
				return 'Administrator';
			case UserType.SOCIAL:
				return 'Socijalni radnik';
			case UserType.HEALTH:
				return 'Zdravstveni radnik';
			default:
				return type;
		}
	};

	if (loading && !users) {
		return (
			<div className='users-list-loading'>
				<p>Učitavanje korisnika...</p>
			</div>
		);
	}

	if (error && !users) {
		return (
			<div className='users-list-error'>
				<p>{error}</p>
				<button onClick={() => loadUsers()} className='user-btn user-btn-primary'>
					Pokušaj ponovo
				</button>
			</div>
		);
	}

	const clearFilters = () => {
		setSearchTerm('');
		setSelectedType('');
		setSelectedStatus('');
		setCurrentPage(1);
		loadUsers(1);
	};

	return (
		<div className='users-list'>
			<div className='users-header'>
				<h2>Upravljanje korisnicima</h2>
				<p>Kreiranje i upravljanje korisničkih naloga</p>
			</div>

			{error && <div className='error-message'>{error}</div>}

			{/* Filters */}
			<div className='filters-section'>
				<h3>Filteri</h3>
				<div className='filters-row'>
					<div className='filter-group'>
						<label htmlFor='search'>Pretraga:</label>
						<input type='text' id='search' value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder='Ime, prezime ili email...' onKeyPress={(e) => e.key === 'Enter' && handleSearch()} />
					</div>

					<div className='filter-group'>
						<label htmlFor='userType'>Tip korisnika:</label>
						<select id='userType' value={selectedType} onChange={(e) => setSelectedType(e.target.value as UserType | '')}>
							<option value=''>Svi tipovi</option>
							<option value={UserType.ADMIN}>Administrator</option>
							<option value={UserType.SOCIAL}>Socijalni radnik</option>
							<option value={UserType.HEALTH}>Zdravstveni radnik</option>
						</select>
					</div>

					<div className='filter-group'>
						<label htmlFor='status'>Status:</label>
						<select id='status' value={selectedStatus.toString()} onChange={(e) => setSelectedStatus(e.target.value === '' ? '' : e.target.value === 'true')}>
							<option value=''>Svi</option>
							<option value='true'>Aktivni</option>
							<option value='false'>Neaktivni</option>
						</select>
					</div>
				</div>

				{/* <div className='filters-actions'>
					<button onClick={clearFilters} className='user-btn user-btn-secondary'>
						Obriši filtere
					</button>
					<button className='user-btn user-btn-secondary' onClick={handleSearch} disabled={loading}>
						{loading ? 'Pretraživanje...' : 'Pretraži'}
					</button>
					<button className='user-btn user-btn-primary' onClick={handleCreateUser}>
						Dodaj novog korisnika
					</button>
				</div> */}
				<div className='filters-actions'>
					<button onClick={clearFilters} className='btn user-btn-secondary'>
						Obriši filtere
					</button>
					<button onClick={handleSearch} disabled={loading} className='btn btn-primary'>
						{loading ? 'Pretraživanje...' : 'Pretraži'}
					</button>
				</div>
			</div>

			{/* Results */}
			{users && (
				<div className='users-table-container'>
					<div className='results-header'>
						<h3>Rezultati ({users.total} korisnika)</h3>

						<div className='results-info' style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
							<button className='btn btn-primary' onClick={handleCreateUser}>
								Dodaj novog korisnika
							</button>
							<div>
								Strana {users.page} od {users.totalPages}
							</div>
						</div>
					</div>

					{users.data.length === 0 ? (
						<div className='no-users'>
							<p>Nema korisnika koji odgovaraju filterima</p>
						</div>
					) : (
						<div className='users-table'>
							<div className='table-header'>
								<div className='col-name'>Ime i prezime</div>
								<div className='col-email'>Email</div>
								<div className='col-phone'>Telefon</div>
								<div className='col-type'>Tip</div>
								<div className='col-institution'>Ustanova</div>
								<div className='col-status'>Status</div>
								<div className='col-actions'>Akcije</div>
							</div>

							<div className='table-body'>
								{users.data.map((user) => (
									<div key={user.id} className='table-row'>
										<div className='col-name'>
											{user.firstName} {user.lastName}
										</div>
										<div className='col-email'>{user.email}</div>
										<div className='col-phone'>{user.phoneNumber}</div>
										<div className='col-type'>
											<span className={`user-type ${user.type}`}>{getUserTypeLabel(user.type)}</span>
										</div>
										<div className='col-institution'>{user.institution}</div>
										<div className='col-status'>
											<span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>{user.isActive ? 'Aktivan' : 'Neaktivan'}</span>
											{user.emailVerified === false && (
												<span className='verification-badge unverified' title='Email nije verifikovan'>
													❌
												</span>
											)}
											{user.emailVerified === true && (
												<span className='verification-badge verified' title='Email je verifikovan'>
													✅
												</span>
											)}
										</div>
										<div className='col-actions'>
											<div className='users-action-buttons'>
												<button className='user-btn  user-btn-info' onClick={() => handleViewUser(user)} title='Pregled'>
													Otvori
												</button>
												{/* <button className='user-btn user-btn-sm user-btn-danger' onClick={() => handleDeleteUser(user.id)} title='Obriši'>
													Obriši
												</button> */}
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Pagination */}
					{users.totalPages > 1 && (
						<div className='pagination'>
							<button className='user-btn user-btn-secondary user-btn-sm' disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
								← Prethodna
							</button>

							<span className='page-info'>
								Strana {currentPage} od {users.totalPages} (ukupno {users.total} korisnika)
							</span>

							<button className='user-btn user-btn-secondary user-btn-sm' disabled={currentPage === users.totalPages} onClick={() => handlePageChange(currentPage + 1)}>
								Sledeća →
							</button>
						</div>
					)}
				</div>
			)}

			{/* Modals */}
			{showCreateModal && (
				<UserForm
					mode='create'
					isOpen={showCreateModal}
					onClose={() => setShowCreateModal(false)}
					onSuccess={() => {
						setShowCreateModal(false);
						loadUsers();
					}}
				/>
			)}

			{showEditModal && selectedUser && (
				<UserForm
					user={selectedUser}
					mode='edit'
					isOpen={showEditModal}
					onClose={() => setShowEditModal(false)}
					onSuccess={() => {
						setShowEditModal(false);
						loadUsers();
					}}
				/>
			)}

			{showDetailModal && selectedUser && (
				<UserDetailView
					user={selectedUser}
					isOpen={showDetailModal}
					onClose={() => setShowDetailModal(false)}
					onEdit={(user) => {
						setShowDetailModal(false);
						setSelectedUser(user);
						setShowEditModal(true);
					}}
					onToggleStatus={handleToggleStatus}
					onDelete={handleDeleteUser}
					onManageVerification={(user) => {
						setShowDetailModal(false);
						setSelectedUser(user);
						setShowVerificationModal(true);
					}}
				/>
			)}

			{showVerificationModal && selectedUser && (
				<VerificationModal
					userId={selectedUser.id}
					userEmail={selectedUser.email}
					userName={`${selectedUser.firstName} ${selectedUser.lastName}`}
					isOpen={showVerificationModal}
					onClose={() => setShowVerificationModal(false)}
					onVerificationSent={() => {
						loadUsers(); // Refresh the list after verification is sent
					}}
				/>
			)}
		</div>
	);
};
